import React, {useState, useEffect} from 'react';
import {View, Animated, StyleSheet, Platform} from 'react-native';
import PropTypes from 'prop-types';
import {isTablet} from 'react-native-device-info';
import {useSelector} from 'react-redux';

import VideoContainer from 'components/Communication/VideoContainer';
import VideoFullscreenButton from './VideoFullscreenButton';
import {getCompactVideoStyle} from './videoLayout';
import {
  getLocalVideoObjectFit,
  isRenderableLocalVideoProducer,
  selectLocalVideoProducer,
} from './localVideoUtils';

const LocalVideo = ({style}) => {
  const {mirrorCamera, cameraEnabled, screenShareEnabled} = useSelector(
    state => state.media,
  );
  const producers = useSelector(state => state.communication.producers);

  const isVideoEnabled = cameraEnabled || screenShareEnabled;
  const [fadeAnimation] = useState(new Animated.Value(isVideoEnabled ? 1 : 0));
  const [position] = useState(new Animated.Value(isVideoEnabled ? 8 : -20));
  const [stream, setStream] = useState(undefined);
  const [isExpanded, setIsExpanded] = useState(false);

  const activeProducer = selectLocalVideoProducer(
    producers,
    screenShareEnabled,
  );
  const track = activeProducer ? activeProducer.track : undefined;
  const activeProducerId = activeProducer ? activeProducer.id : null;
  const isProducerRenderable = isRenderableLocalVideoProducer(activeProducer);
  const waiting = false;

  useEffect(() => {
    if (isProducerRenderable) {
      // eslint-disable-next-line no-undef
      const newStream = new MediaStream();
      newStream.addTrack(track);
      setStream(newStream);
    } else {
      setStream(null);
    }
  }, [isProducerRenderable, track]);

  useEffect(() => {
    if (isVideoEnabled) {
      Animated.parallel([
        Animated.timing(fadeAnimation, {
          toValue: 1,
          duration: 300,
          useNativeDriver: false,
        }),
        Animated.spring(position, {
          toValue: 8,
          duration: 300,
          overshootClamping: true,
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      setIsExpanded(false);
      Animated.parallel([
        Animated.timing(fadeAnimation, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false,
        }),
        Animated.spring(position, {
          toValue: -20,
          duration: 300,
          overshootClamping: true,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [fadeAnimation, isVideoEnabled, position]);

  useEffect(() => setIsExpanded(false), [activeProducerId]);

  const compactVideoContainerStyle = getCompactVideoStyle(
    Platform.OS,
    isTablet(),
  );
  const videoContainerStyle = isExpanded
    ? styles.expandedVideoContainer
    : compactVideoContainerStyle;
  const objectFit = getLocalVideoObjectFit(activeProducer);

  return (
    <View
      style={[
        styles.container,
        isExpanded ? styles.expandedContainer : null,
        style,
      ]}
      pointerEvents="box-none"
    >
      <Animated.View
        style={[
          styles.animatedContainer,
          isExpanded ? styles.expandedAnimatedContainer : null,
          {right: isExpanded ? 0 : position, opacity: fadeAnimation},
        ]}
        pointerEvents="box-none"
      >
        <VideoContainer
          style={videoContainerStyle}
          stream={stream}
          waiting={waiting}
          mirror={objectFit === 'cover' && mirrorCamera}
          objectFit={objectFit}
          pointerEvents="auto"
          zOrder={1}
        />
        {isVideoEnabled && isProducerRenderable ? (
          <VideoFullscreenButton
            expanded={isExpanded}
            onPress={() => setIsExpanded(expanded => !expanded)}
            testID="local-video-fullscreen-button"
          />
        ) : null}
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 110,
    right: 0,
    overflow: 'hidden',
  },
  expandedContainer: {
    bottom: 0,
    backgroundColor: '#000000',
    elevation: 20,
    zIndex: 20,
  },
  animatedContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    position: 'absolute',
    bottom: 0,
  },
  expandedAnimatedContainer: {
    top: 0,
    left: 0,
    bottom: 0,
    alignItems: 'stretch',
    justifyContent: 'center',
    backgroundColor: '#000000',
  },
  expandedVideoContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#000000',
  },
});

LocalVideo.propTypes = {
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number,
    PropTypes.array,
  ]),
};

LocalVideo.defaultProps = {
  style: {},
};

export default LocalVideo;
